import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  CircleX,
  ExternalLink,
  Inbox,
  Search,
  Send,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import CustomPaginator from '../../../components/GlobalComponents/CustomPaginator';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { LeadOrigin, channelLabel, originTarget, originTargetLabel } from './leadOrigin';
import SpendFields, { SpendFormState, checkSpendForm, emptySpendForm } from '../../SpentReports/parts/SpendFields';
import SpendCard from '../../SpentReports/parts/SpendCard';
import { SpendForSync, SyncSpend, fmtINR, spendApi } from '../../SpentReports/parts/spendTypes';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types (mirror models.SheetImport / models.SheetImportRow) ─────────────────
export interface UserRef {
  id: string;
  email?: string | null;
  role?: string | null;
}

export type SheetImportStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface SheetImport {
  id: string;
  sheet_url: string;
  /** Spreadsheet ID — the stable key. */
  sheet_name?: string;
  /** Label only; shared by every sync of the same sheet. */
  display_name?: string;
  detected_channel?: string;
  duplicate_count?: number;
  agency_id: string;
  agency_code?: string;
  imported_by?: UserRef | null;
  status: SheetImportStatus;
  total_rows: number;
  imported_count: number;
  skipped_count: number;
  failed_count: number;
  headers?: string[];
  is_pushed_to_leads: boolean;
  pushed_by?: UserRef | null;
  pushed_at?: string | null;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
}

export interface SheetImportRow {
  id: string;
  import_id: string;
  row_number: number;
  name: string;
  phone: string;
  email?: string | null;
  source?: string | null;
  status?: string | null;
  notes?: string | null;
  city?: string | null;
  interested_in?: string | null;
  extra_fields?: Record<string, string>;
  origin?: LeadOrigin | null;
  is_valid: boolean;
  validation_error?: string;
  created_at: string;
}

interface PageInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** What the logged-in user may do. Spend is shown only when can_manage_spend. */
export interface Viewer {
  role: string;
  can_manage_spend: boolean;
}

interface DetailResponse {
  success: boolean;
  data: SheetImport;
  rows: SheetImportRow[];
  rows_pagination: PageInfo;
  viewer?: Viewer;
}

// ── Icon ──────────────────────────────────────────────────────────────────────
// Same paths as the `sheets` entry in the icon map (24×24, stroke-based).
export const SHEET_ICON_PATHS = [
  'M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2z',
  'M4 9h16',
  'M4 15h16',
  'M10 9v12',
];

export const SheetGlyph: React.FC<{ size?: number; color?: string; strokeWidth?: number }> = ({
  size = 16,
  color = 'currentColor',
  strokeWidth = 2,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0 }}
  >
    {/* tinted header row */}
    <path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v4H4z" fill={color} fillOpacity={0.16} stroke="none" />
    {SHEET_ICON_PATHS.map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

/** White rounded tile with the green sheet glyph — the "Sheets" side of the integration. */
export const SheetTile: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size * 0.27,
      background: 'linear-gradient(160deg,#ffffff,#f0fdf4)',
      border: '1px solid rgba(22,163,74,0.22)',
      boxShadow: '0 6px 18px rgba(22,163,74,0.18), inset 0 1px 0 #fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <SheetGlyph size={size * 0.5} color="#16a34a" strokeWidth={2.1} />
  </div>
);

/** Lime tile — the "Leads" side of the integration. */
export const LeadsTile: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size * 0.27,
      background: `linear-gradient(160deg, ${C.lime}, #a9cc2c)`,
      boxShadow: '0 6px 18px rgba(200,233,75,0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: C.teal,
    }}
  >
    <Users size={size * 0.46} strokeWidth={2.2} />
  </div>
);

// ── Column mapping (mirrors helpers.csvHeaderMap in Go) ───────────────────────
const HEADER_MAP: Record<string, string> = {
  name: 'name', full_name: 'name', lead_name: 'name',
  phone: 'phone', phone_no: 'phone', phone_number: 'phone', mobile: 'phone', mobile_no: 'phone',
  contact: 'phone', contact_number: 'phone',
  email: 'email', email_id: 'email', email_address: 'email',
  source: 'source', lead_source: 'source', referred_by: 'source', reference: 'source',
  status: 'status', lead_status: 'status',
  notes: 'notes', remarks: 'notes', comment: 'notes', comments: 'notes',
  city: 'city', location: 'city', district: 'city',
  interested_in: 'interested_in', product: 'interested_in', product_name: 'interested_in',
  course: 'interested_in', service: 'interested_in', interest: 'interested_in',
};
export const HEADER_SPELLINGS = Object.keys(HEADER_MAP).length;
export const mapHeader = (h: string): string | null =>
  HEADER_MAP[h.trim().toLowerCase().replace(/\s+/g, '_')] ?? null;

// ── Shared styles & helpers (also used by SheetImports.tsx) ───────────────────
export type Tone = { bg: string; color: string; border: string };

export const TONES: Record<'green' | 'red' | 'lime' | 'amber' | 'blue' | 'grey', Tone> = {
  green: { bg: 'rgba(22,163,74,0.08)', color: '#15803d', border: 'rgba(22,163,74,0.25)' },
  red: { bg: 'rgba(220,38,38,0.06)', color: '#c62828', border: 'rgba(220,38,38,0.22)' },
  lime: { bg: C.limeDim, color: '#4a5e00', border: C.limeBorder },
  amber: { bg: 'rgba(234,179,8,0.1)', color: '#92400e', border: 'rgba(234,179,8,0.35)' },
  blue: { bg: 'rgba(59,130,246,0.1)', color: '#1d4ed8', border: 'rgba(59,130,246,0.3)' },
  grey: { bg: 'rgba(156,163,175,0.12)', color: '#4b5563', border: 'rgba(156,163,175,0.3)' },
};

export const IMPORT_STATUS_STYLES: Record<string, Tone> = {
  pending: TONES.grey,
  processing: TONES.blue,
  completed: TONES.green,
  failed: TONES.red,
};
export const STATUS_LABEL: Record<string, string> = {
  pending: 'Queued',
  processing: 'Syncing',
  completed: 'Synced',
  failed: 'Failed',
};

export const pillStyle = (t: Tone): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3.7px',
  padding: '2.8px 8.3px',
  borderRadius: '20px',
  fontSize: '0.63rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  background: t.bg,
  color: t.color,
  border: `1px solid ${t.border}`,
});

export const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const t = IMPORT_STATUS_STYLES[status] || TONES.grey;
  return (
    <span style={pillStyle(t)}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.color }} />
      {STATUS_LABEL[status] || status}
    </span>
  );
};

export const fmtDateTime = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const sheetIdOf = (imp: Pick<SheetImport, 'sheet_name' | 'sheet_url'>) =>
  imp.sheet_name || imp.sheet_url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)?.[1] || imp.sheet_url;

export const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-5)}` : id);

/** What to show for a sheet: its display name, or the short spreadsheet ID. */
export const sheetLabel = (imp: Pick<SheetImport, 'display_name' | 'sheet_name' | 'sheet_url'>) =>
  imp.display_name?.trim() || shortId(sheetIdOf(imp));

export const userLabel = (u?: UserRef | null) => (u?.email ? u.email.split('@')[0] : '—');

export const qualityPct = (valid: number, invalid: number) =>
  valid + invalid > 0 ? Math.round((valid / (valid + invalid)) * 100) : 0;

export const Banner: React.FC<{
  tone: Tone;
  icon: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ tone, icon, children, actions }) => (
  <div
    style={{
      padding: '10.1px 12.9px',
      background: tone.bg,
      border: `1px solid ${tone.border}`,
      borderRadius: '9.2px',
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      flexWrap: 'wrap',
    }}
  >
    <span style={{ color: tone.color, display: 'flex', flexShrink: 0 }}>{icon}</span>
    <span style={{ flex: '1 1 220px', fontSize: '0.73rem', color: tone.color, fontWeight: 600, lineHeight: 1.55 }}>
      {children}
    </span>
    {actions && <div style={{ display: 'flex', gap: '7.4px', flexWrap: 'wrap' }}>{actions}</div>}
  </div>
);

export const StatTile: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({
  label,
  value,
  color = C.teal,
}) => (
  <div
    style={{
      flex: '1 1 96px',
      padding: '10.1px 9.2px',
      background: G.shellBg,
      border: `1px solid ${G.line}`,
      borderRadius: '8.3px',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: '1.15rem', fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
    <div
      style={{
        fontSize: '0.6rem',
        color: C.muted,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        marginTop: '2px',
      }}
    >
      {label}
    </div>
  </div>
);

/** Valid-vs-invalid split bar. */
export const QualityBar: React.FC<{ valid: number; invalid: number; showLabels?: boolean }> = ({
  valid,
  invalid,
  showLabels = true,
}) => {
  const total = valid + invalid;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3.7px', minWidth: '110px' }}>
      {showLabels && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '0.6rem', fontWeight: 700 }}>
          <span style={{ color: '#15803d' }}>{valid} valid</span>
          <span style={{ color: invalid ? '#c62828' : C.muted }}>{invalid} invalid</span>
        </div>
      )}
      <div style={{ display: 'flex', height: '6px', borderRadius: '6px', overflow: 'hidden', background: G.soft }}>
        {total > 0 && (
          <>
            <div style={{ width: `${(valid / total) * 100}%`, background: 'linear-gradient(90deg,#4ade80,#16a34a)' }} />
            <div style={{ width: `${(invalid / total) * 100}%`, background: '#f87171' }} />
          </>
        )}
      </div>
    </div>
  );
};

const SectionTitle: React.FC<{ text: string; extra?: React.ReactNode }> = ({ text, extra }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
    <div
      style={{
        width: '3px',
        height: '14.8px',
        background: `linear-gradient(180deg,${C.lime},#8aaa00)`,
        borderRadius: '3px',
      }}
    />
    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>{text}</span>
    {extra}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

type Validity = 'all' | 'true' | 'false';
const EMPTY_PAGE: PageInfo = { total: 0, page: 1, limit: 20, pages: 0 };

interface SheetImportDetailsModalProps {
  visible: boolean;
  importId: string | null;
  onHide: () => void;
  onPushed?: () => void; // lets the parent refresh its list after a push
  onSpendChanged?: (s: SyncSpend) => void; // admin added / changed the amount
}

const SheetImportDetailsModal: React.FC<SheetImportDetailsModalProps> = ({ visible, importId, onHide, onPushed, onSpendChanged }) => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<SheetImport | null>(null);
  const [rows, setRows] = useState<SheetImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [validity, setValidity] = useState<Validity>('all');
  const [tempSearch, setTempSearch] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState<PageInfo>(EMPTY_PAGE);

  const [confirming, setConfirming] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Spend (admins only)
  const [canSpend, setCanSpend] = useState(false);
  const [spendForm, setSpendForm] = useState<SpendFormState>(emptySpendForm());
  const [staged, setStaged] = useState<SpendForSync | null>(null);
  const [spendRefresh, setSpendRefresh] = useState(0);

  const fetchDetail = useCallback(async () => {
    if (!importId) return;
    try {
      setLoading(true);
      setError(null);
      const p = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (validity !== 'all') p.set('is_valid', validity);
      if (search) p.set('search', search);
      const res = await apiClient.get<DetailResponse>(`/v1/sheet-imports/${importId}?${p}`);
      if (res.data.success) {
        setDetail(res.data.data);
        setRows(res.data.rows ?? []);
        setPagination(res.data.rows_pagination ?? EMPTY_PAGE);
        setCanSpend(!!res.data.viewer?.can_manage_spend);
      } else {
        setError('Failed to load import');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load import');
    } finally {
      setLoading(false);
    }
  }, [importId, page, limit, validity, search]);

  useEffect(() => {
    if (visible && importId) fetchDetail();
  }, [visible, importId, fetchDetail]);

  // Campaigns in the staged rows, for the amount split in the push step.
  useEffect(() => {
    if (!confirming || !canSpend || !importId) return;
    let live = true;
    spendApi
      .get(importId)
      .then((r) => live && setStaged({ data: r.data, campaigns: r.campaigns ?? [], leads_count: r.leads_count ?? 0 }))
      .catch(() => live && setStaged(null));
    return () => {
      live = false;
    };
  }, [confirming, canSpend, importId]);

  const handleClose = () => {
    if (pushing) return;
    onHide();
    setDetail(null);
    setRows([]);
    setError(null);
    setValidity('all');
    setTempSearch('');
    setSearch('');
    setPage(1);
    setPagination(EMPTY_PAGE);
    setConfirming(false);
    setCanSpend(false);
    setSpendForm(emptySpendForm());
    setStaged(null);
  };

  const handleSearch = () => {
    setSearch(tempSearch.trim());
    setPage(1);
  };

  const spendCheck = checkSpendForm(spendForm, staged?.campaigns ?? []);

  const handlePush = async () => {
    if (!importId) return;
    if (canSpend && spendCheck.error) return;
    try {
      setPushing(true);
      // Amount is optional; only admins can send it.
      const body = canSpend && spendCheck.input ? { spend: spendCheck.input } : undefined;
      const res = await apiClient.post(`/v1/sheet-imports/${importId}/push-to-leads`, body);
      const dupes: number = res.data.duplicate_count ?? 0;
      const spent: SyncSpend | undefined = res.data.spend;
      toast.current?.show({
        severity: 'success',
        summary: 'Leads added',
        detail:
          `${res.data.inserted_count ?? 0} leads pushed to your Leads list` +
          (dupes > 0 ? ` · ${dupes} skipped (already a lead)` : '') +
          (spent ? ` · ${fmtINR(spent.amount_incl_gst_paise)} spent, ${fmtINR(spent.cost_per_lead_incl_gst)} per lead` : ''),
        life: 4500,
      });
      if (res.data.spend_error) {
        toast.current?.show({ severity: 'warn', summary: 'Amount not saved', detail: res.data.spend_error, life: 8000 });
      }
      if (spent) onSpendChanged?.(spent);
      setConfirming(false);
      setSpendForm(emptySpendForm());
      setStaged(null);
      setSpendRefresh((n) => n + 1);
      await fetchDetail();
      onPushed?.();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Push failed',
        detail: e.response?.data?.message || e.response?.data?.error || 'Failed to push leads',
        life: 5000,
      });
    } finally {
      setPushing(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const validCount = detail?.imported_count ?? 0;
  const invalidCount = detail?.failed_count ?? 0;
  const headers = detail?.headers ?? [];
  const mappedCount = headers.filter((h) => mapHeader(h) || originTarget(h)).length;

  // ── Table bits ────────────────────────────────────────────────────────────
  const txt = (v?: string | null, mono = false) => (
    <span
      style={{
        color: v ? '#374151' : C.muted,
        fontSize: '0.72rem',
        fontFamily: mono ? 'monospace' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {v || '—'}
    </span>
  );

  const resultTpl = (r: SheetImportRow) =>
    r.is_valid ? (
      <span style={pillStyle(TONES.green)}>
        <CircleCheck size={10} /> Ready
      </span>
    ) : (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          gap: '4.6px',
          color: '#b91c1c',
          fontSize: '0.68rem',
          fontWeight: 600,
          lineHeight: 1.4,
        }}
      >
        <CircleX size={12} color="#ef4444" style={{ flexShrink: 0, marginTop: '1px' }} />
        {r.validation_error || 'Invalid'}
      </span>
    );

  const emptyState = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '30px 0',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'rgba(17,85,80,0.07)',
          border: '1px solid rgba(17,85,80,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Inbox size={20} color={C.teal} />
      </div>
      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: C.teal }}>No rows found</span>
    </div>
  );

  const CH: React.CSSProperties = { whiteSpace: 'nowrap' };

  // ── Push panel ────────────────────────────────────────────────────────────
  const pushPanel = () => {
    if (!detail) return null;

    if (detail.status === 'failed') {
      return (
        <Banner tone={TONES.red} icon={<TriangleAlert size={15} />}>
          This sync failed, so no rows were saved. Check the sheet link and sharing settings, then sync again.
        </Banner>
      );
    }
    if (detail.is_pushed_to_leads) {
      return (
        <Banner
          tone={TONES.green}
          icon={<CircleCheck size={15} />}
          actions={
            <GlobalButton
              label="OPEN THESE LEADS"
              variant="primary"
              icon={ArrowRight}
              onClick={() => navigate(`/leads?sheet_import_id=${detail.id}`)}
            />
          }
        >
          {validCount - (detail.duplicate_count ?? 0)} lead{validCount - (detail.duplicate_count ?? 0) === 1 ? '' : 's'} pushed by{' '}
          {userLabel(detail.pushed_by)} on {fmtDateTime(detail.pushed_at)}.
          {(detail.duplicate_count ?? 0) > 0 && ` ${detail.duplicate_count} row${detail.duplicate_count === 1 ? ' was' : 's were'} already a lead and skipped.`}
        </Banner>
      );
    }
    if (detail.status !== 'completed') {
      return (
        <Banner tone={TONES.blue} icon={<CircleAlert size={15} />}>
          This sheet is still syncing. Refresh in a moment.
        </Banner>
      );
    }
    if (validCount === 0) {
      return (
        <Banner tone={TONES.red} icon={<CircleAlert size={15} />}>
          None of the rows passed validation, so there is nothing to push. Fix the sheet and sync it again.
        </Banner>
      );
    }
    if (confirming) {
      const blocked = canSpend && !!spendCheck.error;
      const confirmBanner = (
        <Banner
          tone={TONES.amber}
          icon={<TriangleAlert size={15} />}
          actions={
            <>
              <GlobalButton
                label="Cancel"
                variant="secondary"
                icon={X}
                onClick={() => setConfirming(false)}
                disabled={pushing}
              />
              <GlobalButton
                label={pushing ? 'PUSHING...' : `YES, PUSH ${validCount}`}
                variant="primary"
                icon={Send}
                onClick={handlePush}
                disabled={pushing || blocked}
                loading={pushing}
              />
            </>
          }
        >
          This creates {validCount} new lead{validCount > 1 ? 's' : ''} and can only be done once for this sync.
          {invalidCount > 0 && ` ${invalidCount} invalid row${invalidCount > 1 ? 's' : ''} will be left out.`}
        </Banner>
      );
      if (!canSpend) return confirmBanner;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {confirmBanner}
          <div style={{ border: `1px dashed ${G.shellBorder}`, borderRadius: '9.2px', padding: '12px', background: '#fcfdfd' }}>
            <div style={{ fontSize: '0.66rem', color: C.sub, marginBottom: '9px', lineHeight: 1.5 }}>
              <strong style={{ color: C.teal }}>Optional · admins only.</strong> Add what you spent to get these leads to track cost
              per lead. Leave it empty to add it later from Sync History.
            </div>
            <SpendFields
              value={spendForm}
              onChange={setSpendForm}
              campaigns={staged?.campaigns ?? []}
              leadsCount={staged?.leads_count ?? validCount}
              leadsNote="(rows already in Leads won't count)"
              disabled={pushing}
            />
          </div>
        </div>
      );
    }
    return (
      <Banner
        tone={TONES.lime}
        icon={<Send size={15} />}
        actions={
          <GlobalButton
            label={`PUSH ${validCount} TO LEADS`}
            variant="primary"
            icon={Send}
            onClick={() => setConfirming(true)}
          />
        }
      >
        {validCount} row{validCount > 1 ? 's are' : ' is'} validated and ready to become leads.
        {invalidCount > 0 && ` ${invalidCount} invalid row${invalidCount > 1 ? 's' : ''} will be skipped.`}
      </Banner>
    );
  };

  // ── Header ────────────────────────────────────────────────────────────────
  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5.5px' }}>
        <SheetTile size={40} />
        <ArrowRight size={13} color={C.muted} />
        <LeadsTile size={40} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>
          Google Sheets Sync
        </div>
        <div
          style={{
            fontSize: '0.71rem',
            color: C.sub,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '5.5px',
            flexWrap: 'wrap',
          }}
        >
          {detail ? (
            <>
              {detail.display_name && <strong style={{ color: C.teal }}>{detail.display_name}</strong>}
              {detail.display_name && <span>·</span>}
              <span style={{ fontFamily: 'monospace' }}>{shortId(sheetIdOf(detail))}</span>
              <span>·</span>
              {detail.detected_channel && (
                <>
                  <span>{channelLabel(detail.detected_channel)}</span>
                  <span>·</span>
                </>
              )}
              <span>{fmtDateTime(detail.created_at)}</span>
              <span>·</span>
              <a
                href={detail.sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#15803d',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '2.8px',
                  textDecoration: 'none',
                }}
              >
                Open sheet <ExternalLink size={10} />
              </a>
            </>
          ) : (
            'Loading…'
          )}
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={handleClose}
        header={headerTemplate}
        className="sim-dialog"
        maskClassName="sim-mask"
        style={{ width: '94vw', maxWidth: '1200px' }}
        modal
        dismissableMask={!pushing}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .sim-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; }
          .sim-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; }
          .sim-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
          .sim-dialog .p-dialog-header-icon { color: ${C.sub}; }
          .sim-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; max-height: 76vh; overflow-y: auto; }
          .sim-dt .p-datatable-thead > tr > th { background:#fafbfb !important; color:#9ca3af !important; font-size:0.62rem !important; font-weight:700 !important; text-transform:uppercase !important; letter-spacing:0.5px !important; border-bottom:1px solid ${G.soft} !important; padding:9px 12px !important; }
          .sim-dt .p-datatable-tbody > tr > td { border-bottom:1px solid ${G.softer} !important; padding:9px 12px !important; vertical-align: top; }
          .sim-dt .p-datatable-tbody > tr:last-child > td { border-bottom:none !important; }
          .sim-dt .p-datatable-tbody > tr.sim-row-invalid { background: rgba(220,38,38,0.025) !important; }
          .sim-dt .p-datatable-emptymessage > td { padding:0 !important; text-align:center !important; border-bottom:none !important; }
          .sim-search:focus-within { border-color: rgba(17,85,80,0.35) !important; }
          .sim-search input::placeholder { color: rgba(17,85,80,0.35); }
        `}</style>

        {loading && !detail ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '240px' }}>
            <ProgressSpinner style={{ width: '34px', height: '34px' }} strokeWidth="3" animationDuration=".8s" />
          </div>
        ) : error && !detail ? (
          <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />
        ) : detail ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />}

            {/* ── Sync overview ────────────────────────────────────────── */}
            <div style={SHELL}>
              <div style={{ ...INNER, padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <SectionTitle text="Sync Overview" />
                  <StatusPill status={detail.status} />
                </div>
                <div style={{ display: 'flex', gap: '6.5px', flexWrap: 'wrap' }}>
                  <StatTile label="Rows Read" value={detail.total_rows} />
                  <StatTile label="Ready" value={validCount} color="#15803d" />
                  <StatTile label="Invalid" value={invalidCount} color="#c62828" />
                  <StatTile label="Data Quality" value={`${qualityPct(validCount, invalidCount)}%`} color={C.tealMid} />
                </div>
                {detail.total_rows > 0 && <QualityBar valid={validCount} invalid={invalidCount} showLabels={false} />}
                {pushPanel()}
                {canSpend && detail.is_pushed_to_leads && (
                  <SpendCard importId={detail.id} sheetName={sheetLabel(detail)} refreshKey={spendRefresh} onChanged={onSpendChanged} />
                )}
              </div>
            </div>

            {/* ── Column mapping ───────────────────────────────────────── */}
            {headers.length > 0 && (
              <div style={SHELL}>
                <div style={{ ...INNER, padding: '14px' }}>
                  <SectionTitle
                    text="Column Mapping"
                    extra={
                      <span style={pillStyle(TONES.lime)}>
                        {mappedCount}/{headers.length} matched
                      </span>
                    }
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6.5px', marginTop: '11px' }}>
                    {headers.map((h, i) => {
                      // lead field (green) → origin column (blue) → custom field (grey).
                      // Nothing is dropped: unknown columns become custom fields on the lead.
                      const field = mapHeader(h);
                      const origin = field ? null : originTarget(h);
                      const tone = field ? TONES.green : origin ? TONES.blue : null;
                      return (
                        <div
                          key={`${h}-${i}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5.5px',
                            padding: '4.6px 9.2px',
                            borderRadius: '7.4px',
                            fontSize: '0.68rem',
                            background: tone ? tone.bg : G.shellBg,
                            border: `1px solid ${tone ? tone.border : G.line}`,
                          }}
                        >
                          <SheetGlyph size={11} color={tone ? tone.color : C.muted} />
                          <span style={{ fontWeight: 600, color: '#374151' }}>{h || '(blank)'}</span>
                          <ArrowRight size={10} color={tone ? tone.color : C.muted} />
                          <span
                            style={{
                              fontWeight: 700,
                              fontFamily: tone ? 'monospace' : undefined,
                              color: tone ? tone.color : C.sub,
                            }}
                          >
                            {field ?? (origin ? originTargetLabel(origin) : 'custom field')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '9px', fontSize: '0.62rem', color: C.muted }}>
                    <span>
                      <strong style={{ color: TONES.green.color }}>green</strong> lead field
                    </span>
                    <span>
                      <strong style={{ color: TONES.blue.color }}>blue</strong> where the lead came from (campaign, form, ad…)
                    </span>
                    <span>
                      <strong style={{ color: C.sub }}>grey</strong> kept as a custom field on the lead
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Rows ─────────────────────────────────────────────────── */}
            <div style={SHELL}>
              <div style={{ ...INNER, overflow: 'hidden' }}>
                <div
                  style={{
                    padding: '10px 14px',
                    borderBottom: `1px solid ${G.soft}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '9.2px',
                    flexWrap: 'wrap',
                  }}
                >
                  <SectionTitle text="Synced Rows" />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px', flexWrap: 'wrap' }}>
                    {/* Valid / invalid filter */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '1.8px',
                        background: G.shellBg,
                        borderRadius: '7.4px',
                        padding: '2.8px',
                        border: `1px solid ${G.line}`,
                      }}
                    >
                      {(
                        [
                          { v: 'all', l: 'All', n: detail.total_rows },
                          { v: 'true', l: 'Ready', n: validCount },
                          { v: 'false', l: 'Invalid', n: invalidCount },
                        ] as { v: Validity; l: string; n: number }[]
                      ).map((o) => {
                        const on = validity === o.v;
                        return (
                          <button
                            key={o.v}
                            onClick={() => {
                              setValidity(o.v);
                              setPage(1);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4.6px',
                              padding: '3.7px 9.2px',
                              borderRadius: '5.5px',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              background: on ? C.teal : 'transparent',
                              color: on ? 'white' : 'rgba(17,85,80,0.6)',
                              border: on ? `1px solid ${C.teal}` : '1px solid transparent',
                            }}
                          >
                            {o.l}
                            <span style={{ opacity: 0.75, fontWeight: 700 }}>{o.n}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Search */}
                    <div
                      className="sim-search"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6.5px',
                        height: '31px',
                        width: '210px',
                        maxWidth: '100%',
                        padding: '0 9.2px',
                        background: 'white',
                        border: `1px solid ${G.line}`,
                        borderRadius: '7.4px',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      <Search size={11} style={{ color: C.muted }} />
                      <input
                        value={tempSearch}
                        onChange={(e) => setTempSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Name, phone, city, interest…"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          color: C.teal,
                          fontSize: '0.7rem',
                        }}
                      />
                      {(tempSearch || search) && (
                        <button
                          onClick={() => {
                            setTempSearch('');
                            setSearch('');
                            setPage(1);
                          }}
                          title="Clear"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: C.muted,
                            display: 'flex',
                          }}
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <DataTable
                    value={rows}
                    dataKey="id"
                    loading={loading}
                    emptyMessage={emptyState}
                    className="sim-dt"
                    rowClassName={(r: SheetImportRow) => (r.is_valid ? '' : 'sim-row-invalid')}
                    style={{ fontSize: '0.74rem' }}
                  >
                    <Column
                      header="Row"
                      body={(r: SheetImportRow) => (
                        <span style={{ color: C.muted, fontFamily: 'monospace', fontSize: '0.7rem' }}>{r.row_number}</span>
                      )}
                      headerStyle={{ ...CH, width: '3.4rem' }}
                    />
                    <Column
                      header="Name"
                      body={(r: SheetImportRow) => (
                        <span style={{ color: r.name ? C.teal : C.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {r.name || '—'}
                        </span>
                      )}
                      headerStyle={CH}
                    />
                    <Column header="Phone" body={(r: SheetImportRow) => txt(r.phone, true)} headerStyle={CH} />
                    <Column header="Email" body={(r: SheetImportRow) => txt(r.email)} headerStyle={CH} />
                    <Column header="City" body={(r: SheetImportRow) => txt(r.city)} headerStyle={CH} />
                    <Column header="Interested In" body={(r: SheetImportRow) => txt(r.interested_in)} headerStyle={CH} />
                    <Column header="Source" body={(r: SheetImportRow) => txt(r.source)} headerStyle={CH} />
                    <Column
                      header="Campaign"
                      body={(r: SheetImportRow) => txt(r.origin?.campaign?.name || r.origin?.campaign?.id)}
                      headerStyle={CH}
                    />
                    <Column header="Status" body={(r: SheetImportRow) => txt(r.status)} headerStyle={CH} />
                    <Column header="Check" body={resultTpl} headerStyle={{ ...CH, minWidth: '150px' }} />
                  </DataTable>
                </div>

                <CustomPaginator
                  page={page}
                  totalPages={pagination.pages}
                  totalRecords={pagination.total}
                  pageSize={limit}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setLimit(size);
                    setPage(1);
                  }}
                  pageSizeOptions={[20, 50, 100, 200]}
                  showInfoBar
                />
              </div>
            </div>
          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default SheetImportDetailsModal;